import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Paper
} from '@mui/material';
import LoadingIndicator from './LoadingIndicator';
import { useAuth } from '../contexts/AuthContext';
import { useAdmin } from '../contexts/AdminContext';
import {
  getOrganizationProfile,
  updateOrganizationProfile,
  getOrganizationProfileValidationOptions
} from '../services/organizationProfileService';
import { getProfile } from '../services/authApiService';
import linesOverlay from '../assets/images/Lines-no_background.png';

const LANGUAGE_FLAGS = {
  en: '🇬🇧',
  fi: '🇫🇮',
  sv: '🇸🇪'
};

const ORGANIZATION_SIZE_OPTIONS = [
  '1-10 employees',
  '11-50 employees',
  '51-200 employees',
  '201-1000 employees',
  '1000+ employees'
];

const heroBoxStyles = {
  backgroundColor: '#021c2d',
  backgroundImage: `url(${linesOverlay})`,
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  color: 'white',
  padding: { xs: 1.5, md: 2 },
  position: 'relative',
  zIndex: 1,
  borderRadius: '16px 16px 0 0'
};

const formContainerStyles = {
  padding: { xs: 3, md: 5 },
  backgroundColor: 'white',
  borderRadius: '0 0 16px 16px'
};

const OrganizationProfile = () => {
  const { user, isDemoMode } = useAuth();
  const { isAdmin } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [noOrgError, setNoOrgError] = useState(null);

  const [profile, setProfile] = useState({
    name: '',
    industry: '',
    default_language: 'en',
    context: '',
    domains: [],
    size: '',
    departments: ''
  });

  const [validationOptions, setValidationOptions] = useState({
    industries: [],
    languages: []
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      try {
        setLoading(true);
        setError(null);
        setNoOrgError(null);

        // Check if user is admin, manager, or demo
        const userProfile = await getProfile();
        const isManager = userProfile.access_level === 'manager' || userProfile.access_level === 'demo';

        if (!isAdmin && !isManager) {
          setHasAccess(false);
          setLoading(false);
          return;
        }

        setHasAccess(true);

        // Fetch organization profile
        const orgProfile = await getOrganizationProfile();
        setProfile({
          name: orgProfile.name || '',
          industry: orgProfile.industry || '',
          default_language: orgProfile.default_language || 'en',
          context: orgProfile.context || '',
          domains: orgProfile.domains || [],
          size: orgProfile.size || '',
          departments: Array.isArray(orgProfile.departments) ? orgProfile.departments.join(', ') : (orgProfile.departments || '')
        });

        // Fetch validation options
        const options = await getOrganizationProfileValidationOptions();
        setValidationOptions(options);

      } catch (err) {
        console.error('Failed to fetch organization profile:', err);

        // Check if this is a "no organization" error
        if (err.message.includes('not associated with any organization') ||
            err.message.includes('individual users pool')) {
          setNoOrgError(err.message);
          setHasAccess(isAdmin); // Admins can see the error, non-managers cannot
        } else {
          setError(err.message || 'Failed to load organization profile');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, isAdmin]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile((prevProfile) => ({
      ...prevProfile,
      [name]: value
    }));
  };

  const handleLanguageSelect = (code) => {
    setProfile((prevProfile) => ({
      ...prevProfile,
      default_language: code
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    // Validation
    if (!profile.name.trim()) {
      setError('Organization name is required');
      return;
    }
    if (!profile.industry) {
      setError('Please select an industry');
      return;
    }

    setSaving(true);
    try {
      await updateOrganizationProfile({
        name: profile.name.trim(),
        industry: profile.industry,
        default_language: profile.default_language,
        context: profile.context.trim(),
        size: profile.size,
        departments: profile.departments.trim()
      });

      setSuccess(true);
      // Auto-hide success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000);

    } catch (err) {
      console.error('Failed to update organization profile:', err);
      setError(err.message || 'Failed to update organization profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingIndicator />;
  }

  if (!hasAccess) {
    return (
      <Alert severity="error">
        <Typography variant="h6" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1">
          Only organisation managers and administrators can access organisation settings.
        </Typography>
      </Alert>
    );
  }

  if (noOrgError) {
    return (
      <Alert severity="info">
        <Typography variant="h6" gutterBottom>
          No Organisation
        </Typography>
        <Typography variant="body1">
          {noOrgError}
        </Typography>
        {isAdmin && (
          <Typography variant="body2" sx={{ mt: 2 }}>
            As an admin, you can manage organisations from the Admin Dashboard.
          </Typography>
        )}
      </Alert>
    );
  }

  return (
    <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid rgba(0, 0, 0, 0.12)' }}>
      <Box sx={heroBoxStyles}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: 'Bebas Neue',
            letterSpacing: 1
          }}
        >
          Organisation Profile
        </Typography>
      </Box>

      <Box sx={formContainerStyles}>
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(false)}>
            Organisation profile updated successfully!
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            mb: 3
          }}>
            <Typography variant="h5" sx={{ fontFamily: 'Bebas Neue', letterSpacing: 1 }}>
              Organisation Details
            </Typography>
            {validationOptions.languages.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: '#0d1b2a'
                  }}
                >
                  Interview language
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  {validationOptions.languages.map((lang) => (
                    <Tooltip key={lang.code} title={isDemoMode ? "Editing disabled in demo mode" : lang.name} placement="top">
                      <Box
                        onClick={() => !isDemoMode && handleLanguageSelect(lang.code)}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          cursor: isDemoMode ? 'not-allowed' : 'pointer',
                          padding: '4px 8px',
                          transition: 'transform 0.2s',
                          opacity: isDemoMode ? 0.6 : 1,
                          '&:hover': {
                            transform: isDemoMode ? 'none' : 'scale(1.05)'
                          }
                        }}
                      >
                        <Box sx={{
                          fontSize: '1.4rem',
                          opacity: 1
                        }}>
                          {LANGUAGE_FLAGS[lang.code] || '🌐'}
                        </Box>
                        <Box sx={{
                          width: '100%',
                          height: '2px',
                          backgroundColor: profile.default_language === lang.code ? '#d1d5db' : 'transparent',
                          borderRadius: '1px',
                          marginTop: '2px',
                          transition: 'background-color 0.2s'
                        }} />
                      </Box>
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          <Tooltip title={isDemoMode ? "Editing disabled in demo mode" : ""} placement="top">
            <TextField
              label="Organization Name"
              placeholder="e.g. Acme Corporation"
              variant="outlined"
              fullWidth
              name="name"
              value={profile.name}
              onChange={handleChange}
              required
              disabled={isDemoMode}
              sx={{ mb: 3 }}
            />
          </Tooltip>

          {profile.domains && profile.domains.length > 0 && (
            <TextField
              label="Email Domain"
              variant="outlined"
              fullWidth
              value={profile.domains.join(', ')}
              disabled
              sx={{
                mb: 3,
                backgroundColor: '#f5f5f5',
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)'
                }
              }}
              helperText="Email domains are set automatically at login and cannot be changed here."
            />
          )}

          <Tooltip title={isDemoMode ? "Editing disabled in demo mode" : ""} placement="top">
            <FormControl fullWidth sx={{ mb: 3 }} required>
              <InputLabel id="industry-label">Industry</InputLabel>
              <Select
                labelId="industry-label"
                label="Industry"
                name="industry"
                value={profile.industry}
                onChange={handleChange}
                disabled={isDemoMode}
              >
                <MenuItem value="">
                  <em>Select Industry</em>
                </MenuItem>
                {validationOptions.industries.map((industry) => (
                  <MenuItem key={industry} value={industry}>
                    {industry}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Tooltip>

          <Tooltip title={isDemoMode ? "Editing disabled in demo mode" : ""} placement="top">
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="size-label">Organization Size</InputLabel>
              <Select
                labelId="size-label"
                label="Organization Size"
                name="size"
                value={profile.size}
                onChange={handleChange}
                disabled={isDemoMode}
              >
                <MenuItem value="">
                  <em>Select Size</em>
                </MenuItem>
                {ORGANIZATION_SIZE_OPTIONS.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Tooltip>

          <Tooltip title={isDemoMode ? "Editing disabled in demo mode" : ""} placement="top">
            <TextField
              label="Departments (Optional)"
              placeholder="e.g., Sales, Marketing, Engineering, HR"
              variant="outlined"
              fullWidth
              name="departments"
              value={profile.departments}
              onChange={handleChange}
              disabled={isDemoMode}
              sx={{ mb: 3 }}
              helperText="Enter department names separated by commas. These will be available for users during onboarding."
            />
          </Tooltip>

          <Tooltip title={isDemoMode ? "Editing disabled in demo mode" : ""} placement="top">
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Organization Context (Optional)"
              placeholder="e.g., We're a global tech company specializing in AI-driven healthcare solutions, with offices in 15 countries and a focus on medical data analytics."
              name="context"
              value={profile.context}
              onChange={handleChange}
              disabled={isDemoMode}
              sx={{ mb: 3 }}
              helperText="This context helps the AI provide more relevant guidance during assessments"
            />
          </Tooltip>

          <Tooltip title={isDemoMode ? "Editing disabled in demo mode" : ""} placement="top">
            <span>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={saving || isDemoMode}
                sx={{
                  backgroundColor: '#021c2d',
                  '&:hover': { backgroundColor: '#042a44' },
                  textTransform: 'none',
                  fontSize: '1rem',
                  px: 4
                }}
              >
                {saving ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
};

export default OrganizationProfile;
